import { useNavigate, useParams } from 'react-router-dom'
import { SubmitHandler } from 'react-hook-form'

import { TaskForm } from '@containers/Forms/Task'
import { TTaskForm } from '@containers/Forms/Task/schema'
import { useAlerts } from '@hooks/alerts'
import { useOptimisticDeleteTask } from '@hooks/task/delete/deleteFromList'
import { useFindTask } from '@hooks/task/find'
import { useUpdateTaskResource } from '@hooks/task/put/editResource'
import { RoutesParamsList } from '@services/routes'

import { useStartDateAndTime } from '../startDateAndTime'

export function EditTaskFormScreen() {
  const { showDeleteAlert, showDismissAlert } = useAlerts()
  const navigate = useNavigate()
  const params = useParams()
  const taskId = Number(params.taskId)
  const { mutateAsync: putTask } = useUpdateTaskResource()
  const { mutateAsync: deleteTask } = useOptimisticDeleteTask()

  const { mergeDateAndTime } = useStartDateAndTime()

  const { data: rawTask } = useFindTask(taskId)
  const task = rawTask ?? ({} as NonNullable<typeof rawTask>)

  const handleSave: SubmitHandler<TTaskForm> = async (data: TTaskForm) => {
    const mergedTask = { ...task, ...data }
    const {
      startTime,
      selectedDate,
      permitNeeded,
      permitTypeIds,
      permitStatus,
      ...restData
    } = mergedTask

    const changes = {
      ...restData,
      startTime: new Date(mergeDateAndTime({ startTime, selectedDate })),
      permitTypeIds: permitNeeded === 1 ? permitTypeIds : [],
      permitStatus: permitNeeded === 1 ? permitStatus : 2,
      permitNeeded,
    }

    const payload = {
      id: task.id,
      task: changes,
    }

    try {
      await putTask(payload)
      navigate(-1)
    } catch {
      window.alert('Error\n\nFailed to save task. Please try again.')
    }
  }

  async function deleteTaskPress() {
    try {
      await deleteTask(task.id)
      navigate('/')
    } catch {
      window.alert('Error\n\nFailed to delete task. Please try again.')
    }
  }

  function handleShowAlertOnDeleteTask() {
    showDeleteAlert('Task', deleteTaskPress)
  }

  function handleShowAlertOnAssignmentDismiss() {
    showDismissAlert(() => navigate(-1))
  }

  return (
    <TaskForm
      onSave={handleSave}
      onDelete={handleShowAlertOnDeleteTask}
      onClose={handleShowAlertOnAssignmentDismiss}
      task={task}
      title="Edit Task"
      onAddAssignment={() => navigate(`/task/${taskId}/add-assignment`)}
    />
  )
}
